import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ClipboardCheck, Loader2, Plus } from 'lucide-react';
import { api } from '../../api/client';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstant } from '../../lib/calendar-date';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  type TripInspectionListRow, type ReportOutcome, type TripInspectionType,
  TRIP_TYPES, TRIP_TYPE_LABEL, personName, reportOutcome, tripTypeLabel, UNIT_ROLE_LABEL,
} from '../../lib/trip-inspections';

/**
 * The office register of pre-trip and post-trip inspections (api#855, 0437).
 *
 * The band carries NO stat strip. The hero circle's clearance was measured
 * against Quotes' and Rate Sheets' stat text only (CLAUDE.md), and a new page
 * with new stat labels would owe a fresh sweep; the counts this page has are
 * on its chips, in the body, where there is no circle.
 *
 * Chip counts are a TALLY OF THE LOADED ROWS. No endpoint returns them, and
 * `GET /trucking/trip-inspections` is capped at 500 rows, newest first — the
 * footer says "showing N" so a tenant past the cap is not told it has N.
 *
 * `?fleet_id=` narrows the list to one unit; the Fleet page links here with it.
 *
 * TWO CHIP ROWS, ONE LIST. The type chips (pre-trip / post-trip) and the
 * outcome chips both narrow the same rows, so they are chips, not tabs. Type
 * counts are over every loaded row; outcome counts are over the rows the
 * chosen type leaves, so each number is what that chip would show.
 */

type FilterKey = 'all' | 'major' | 'minor' | 'open';
const FILTERS: { key: FilterKey; label: string; match: (r: TripInspectionListRow) => boolean }[] = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'major', label: 'Major defects', match: (r) => reportOutcome(r) === 'major' },
  { key: 'minor', label: 'Minor only', match: (r) => reportOutcome(r) === 'minor' },
  { key: 'open', label: 'Uncertified', match: (r) => r.open_defects > 0 },
];

/** Five signal hues and no orange: minor is amber, major red, clean green. */
const OUTCOME: Record<ReportOutcome, { label: string; fill: string; rail: string }> = {
  no_defects: { label: 'No defects', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  minor: { label: 'Defects', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  major: { label: 'Major defect', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};

export function OutcomeBadge({ outcome }: { outcome: ReportOutcome }) {
  const s = OUTCOME[outcome];
  return (
    <span className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${s.fill}`}>
      {s.label}
    </span>
  );
}

type TypeKey = 'all' | TripInspectionType;
const TYPE_FILTERS: { key: TypeKey; label: string }[] = [
  { key: 'all', label: 'All types' },
  ...TRIP_TYPES.map((t) => ({ key: t as TypeKey, label: TRIP_TYPE_LABEL[t] })),
];
const typeMatch = (key: TypeKey) => (r: TripInspectionListRow) => key === 'all' || r.inspection_type === key;

const unitsText = (r: TripInspectionListRow) =>
  r.units.length ? r.units.map((u) => `${u.unit_number} (${UNIT_ROLE_LABEL[u.unit_role] ?? '—'})`).join(', ') : '—';

export default function TripInspectionsScreen() {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const [params] = useSearchParams();
  const fleetId = params.get('fleet_id');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [typeFilter, setTypeFilter] = useState<TypeKey>('all');

  const query = useQuery<TripInspectionListRow[]>({
    queryKey: ['trip-inspections', 'list', fleetId ?? 'all'],
    queryFn: () => api.trucking.tripInspections.list(fleetId ? { fleet_id: fleetId } : undefined).then((r) => r.data?.data ?? []),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const rows = useMemo(() => query.data ?? [], [query.data]);
  const typed = useMemo(() => rows.filter(typeMatch(typeFilter)), [rows, typeFilter]);
  const shown = useMemo(() => typed.filter(FILTERS.find((f) => f.key === filter)!.match), [typed, filter]);
  const fleetLabel = fleetId ? rows.flatMap((r) => r.units).find((u) => u.fleet_id === fleetId)?.unit_number : null;

  const open = (id: string) => navigate(`/trucking/trip-inspections/${id}`);
  const rowKeys = (id: string) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(id); }
  };

  return (
    <div data-testid="trip-inspections-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Fleet safety"
        title="Trip Inspections"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <button type="button" onClick={() => navigate('/trucking/trip-inspections/new')} className="fi-action fi-action--standard fi-action--primary">
            <Plus className="size-4" aria-hidden="true" /> New inspection
          </button>
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {fleetId && (
            <p className="text-sm text-fieldiq-text">
              Showing reports that include unit {fleetLabel ?? '—'}.{' '}
              <button type="button" className="font-semibold underline" onClick={() => navigate('/trucking/trip-inspections')}>Show all units</button>
            </p>
          )}

          <div className="scrollbar-hide flex gap-2 overflow-x-auto" data-testid="trip-type-chips">
            {TYPE_FILTERS.map((f) => {
              const active = typeFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setTypeFilter(f.key)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    active ? 'bg-fi-amber text-fi-amber-ink' : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {f.label}
                  {query.data && (
                    <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>{rows.filter(typeMatch(f.key)).length}</span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="scrollbar-hide flex gap-2 overflow-x-auto">
            {FILTERS.map((f) => {
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    active ? 'bg-fi-amber text-fi-amber-ink' : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {f.label}
                  {query.data && (
                    <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>{typed.filter(f.match).length}</span>
                  )}
                </button>
              );
            })}
          </div>

          <section data-testid="trip-register" className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Trip inspection register</p>
              <h2 className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Reports</h2>
            </header>
            {query.isLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="size-6 animate-spin text-fieldiq-muted" /></div>
            ) : failure ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="trip inspections" allowed="drivers and office roles" />
            ) : rows.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <ClipboardCheck className="mx-auto mb-3 size-10 text-fieldiq-muted" aria-hidden="true" />
                <p className="text-sm text-fieldiq-text">No trip inspections have been filed{fleetId ? ' for this unit' : ''}.</p>
              </div>
            ) : shown.length === 0 ? (
              <p className="px-4 py-12 text-center text-sm text-fieldiq-text">No reports match this filter.</p>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[860px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Inspected</th>
                        <th scope="col" className="px-4 py-3">Type</th>
                        <th scope="col" className="px-4 py-3">Units</th>
                        <th scope="col" className="px-4 py-3">Driver</th>
                        <th scope="col" className="px-4 py-3">Location</th>
                        <th scope="col" className="px-4 py-3">Result</th>
                        <th scope="col" className="px-4 py-3 text-right">Uncertified</th>
                        <th scope="col" className="w-[50px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((r) => {
                        const outcome = reportOutcome(r);
                        return (
                          <tr
                            key={r.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => open(r.id)}
                            onKeyDown={rowKeys(r.id)}
                            className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${OUTCOME[outcome].rail}`}
                          >
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{formatInstant(r.inspected_at, timeZone) ?? '—'}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{tripTypeLabel(r.inspection_type)}</td>
                            <td className="px-4 py-3 text-sm font-bold text-fieldiq-text">{unitsText(r)}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">
                              {personName(r.driver_first_name, r.driver_last_name)}
                              {r.entered_by_office && <span className="ml-2 text-xs text-fieldiq-muted">Entered by office</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-fieldiq-muted">{r.location || '—'}</td>
                            <td className="px-4 py-3"><OutcomeBadge outcome={outcome} /></td>
                            <td className="px-4 py-3 text-right text-sm tabular-nums text-fieldiq-text">{r.open_defects}</td>
                            <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {shown.map((r) => (
                    <div
                      key={r.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(r.id)}
                      onKeyDown={rowKeys(r.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-sm font-bold text-fieldiq-text">{unitsText(r)}</span>
                        <OutcomeBadge outcome={reportOutcome(r)} />
                      </div>
                      <p className="mt-1 text-xs text-fieldiq-muted">
                        {tripTypeLabel(r.inspection_type)} · {formatInstant(r.inspected_at, timeZone) ?? '—'} · {personName(r.driver_first_name, r.driver_last_name)}
                        {r.entered_by_office ? ' · Entered by office' : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
            {query.data && rows.length > 0 && (
              <footer className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">
                Showing {shown.length} of {rows.length} reports loaded, newest first{rows.length >= 500 ? ' — the 500 most recent' : ''}.
              </footer>
            )}
          </section>
        </div>
      </section>
    </div>
  );
}
